import { useDisclosure } from '@chakra-ui/react';
import { AccountStatusEnum, IAccountRespSchema } from '@monetize/types/app';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  MBox,
  MFlex,
  MHStack,
  MPageHeader,
  MPageSearchInput,
} from '../../../components/Monetize';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import {
  SegmentedControl,
  SegmentedControlOption,
} from '../../../components/Monetize/SegmentedControl';
import { RenewalModal } from '../../../components/RenewalModal/RenewalModal';
import { ROUTES } from '../../../constants';
import { getAccountQuotesRoute } from '../../../constants/routes';
import {
  IContractWithBillGroup,
  useContractsData,
} from '../../../hooks/AccountDetail';
import { useAccountContractLineages } from '../../../hooks/useContractLineages';
import { useACL } from '../../../services/acl/acl';
import { contractsViewModeState } from '../../../store/global.store';
import {
  combineFilterStateKey,
  ContractsViewMode,
  FilterStateKeys,
} from '../../../store/store.types';
import ContractActions from '../../Quotes/Quote/ContractActions';
import {
  CONTRACT_LIST_ACCOUNT_CATALOG,
  ContractListAccountColumnContext,
} from './contractListAccount.columns';
import { ContractLineageCards } from './ContractLineageCards';

const VIEW_MODE_OPTIONS: SegmentedControlOption<ContractsViewMode>[] = [
  { label: 'Cards', value: 'cards' },
  { label: 'Table', value: 'table' },
];

const ContractListAccount = ({
  account,
  billGroupId,
}: {
  account: IAccountRespSchema;
  billGroupId?: string;
}) => {
  const navigate = useNavigate();
  const { id: accountId } = account;

  const tableRef = useRef<DataTableRef | null>(null);

  const [selectedContract, setSelectedContract] =
    useState<IContractWithBillGroup | null>(null);

  const {
    open: isOpenRenewalModal,
    onOpen: onOpenRenewalModal,
    onClose: onCloseRenewalModal,
  } = useDisclosure();

  // Remembered in localStorage so the user's last pick becomes their default
  const [viewMode, setViewMode] = useAtom(contractsViewModeState);
  const isCardsView = viewMode === 'cards';

  const {
    filters,
    setFilters,
    searchKey,
    searchTerm,
    setSearchTerm,
    onResetFilter,
    listData,
    pager,
    setPager,
    loading,
    refetch,
  } = useContractsData(
    accountId,
    billGroupId,
    combineFilterStateKey(FilterStateKeys.CONTRACT_LIST, { accountId }),
    // Cards fetch every contract themselves; only page the table when shown
    { enabled: !isCardsView },
  );

  const {
    lineages,
    today,
    isLoading: isLineagesLoading,
    isError: isLineagesError,
    refetch: refetchLineages,
  } = useAccountContractLineages({ accountId, billGroupId });

  const { canDo } = useACL();
  const canUpdateSales = canDo([['sales', 'update']]);
  const canManageInvoices =
    canDo([['billing', 'update']]) &&
    account.status === AccountStatusEnum.ACTIVE;

  const handleOpenContract = useCallback(
    (contractId: string) => {
      if (!canUpdateSales) {
        return;
      }
      navigate(
        billGroupId
          ? ROUTES.getBillGroupContractsEditRoute(billGroupId, contractId)
          : ROUTES.getAccountContractEditRoute(accountId, contractId),
      );
    },
    [accountId, billGroupId, canUpdateSales, navigate],
  );

  const handleEarlyRenewal = useCallback(
    (data: IContractWithBillGroup) => {
      setSelectedContract(data);
      onOpenRenewalModal();
    },
    [onOpenRenewalModal],
  );

  const actionBodyTemplate = useCallback(
    (data: IContractWithBillGroup) =>
      canUpdateSales && (
        <ContractActions
          contract={data}
          handleEarlyRenewal={handleEarlyRenewal}
        />
      ),
    [canUpdateSales, handleEarlyRenewal],
  );

  const columnContext = useMemo<ContractListAccountColumnContext>(
    () => ({ defaultCurrency: account.defaultCurrency, actionBodyTemplate }),
    [account.defaultCurrency, actionBodyTemplate],
  );
  const { columns, state: columnState } = useTableColumns(
    CONTRACT_LIST_ACCOUNT_CATALOG,
    columnContext,
  );

  // Hide the header only for an account with no contracts at all, so the view
  // toggle stays reachable while either view loads or after it fails
  const hasNoContracts =
    !isLineagesLoading && !isLineagesError && lineages.length === 0;

  const handleResetFilter = () => {
    onResetFilter();
    tableRef.current?.resetFilters();
  };

  return (
    <MBox>
      <MPageHeader title="Contracts" hideContent={hasNoContracts}>
        <MFlex>
          <MHStack gap="2">
            {!isCardsView && (
              <MPageSearchInput
                placeholderKey="Contract Name"
                value={searchTerm}
                count={listData?.totalElements}
                mode="alwaysExpanded"
                onChange={setSearchTerm}
              />
            )}
            <SegmentedControl
              name="contractsViewMode"
              label="Contracts view"
              options={VIEW_MODE_OPTIONS}
              value={viewMode}
              onChange={setViewMode}
            />
            <RefreshTableButton
              refetch={isCardsView ? refetchLineages : refetch}
            />
            {/* Cards show every contract, so the table's hidden filters don't apply */}
            <ExportEntityButton
              entity="accountContracts"
              filters={isCardsView ? undefined : filters}
              searchKey={isCardsView ? undefined : searchKey}
              searchTerm={isCardsView ? undefined : searchTerm}
              sortField={isCardsView ? undefined : pager.sortField}
              sortOrder={isCardsView ? undefined : pager.sortOrder}
              endpointParams={[accountId]}
              getFilename={() => `contracts-${accountId}.csv`}
            />
          </MHStack>
        </MFlex>
      </MPageHeader>
      {isCardsView ? (
        <ContractLineageCards
          accountId={accountId}
          lineages={lineages}
          today={today}
          isLoading={isLineagesLoading}
          isError={isLineagesError}
          onRetry={refetchLineages}
          onSelectContract={canUpdateSales ? handleOpenContract : undefined}
          canManageInvoices={canManageInvoices}
        />
      ) : (
        <DataTable
          ref={tableRef}
          columns={columns}
          state={columnState}
          data={listData?.content ?? []}
          pinnedRightColumns={[ACTION_COLUMN_KEY]}
          totalElements={listData?.totalElements}
          totalPages={listData?.totalPages}
          pager={pager}
          setPager={setPager}
          initialSortingState={[{ id: 'startDate', desc: true }]}
          initialFilters={filters}
          onFiltersChange={(newFilters) => {
            setFilters(newFilters);
            setPager({ ...pager, page: 0, first: 0 });
          }}
          onRowClick={({ id }) => handleOpenContract(id)}
          isLoading={loading}
          emptyProps={{
            mainMessage: 'Looks like there are no contracts here.',
            btnLabel: 'View Quotes',
            to: getAccountQuotesRoute(accountId),
          }}
          emptyFilterResultsProps={{ btnLabel: 'Reset Filters' }}
          resetFilter={handleResetFilter}
        />
      )}
      {selectedContract && isOpenRenewalModal && (
        <RenewalModal
          contract={selectedContract}
          onClose={onCloseRenewalModal}
        />
      )}
      <Outlet />
    </MBox>
  );
};

export default ContractListAccount;
